import React, { useState, useCallback } from "react";
import { LockIcon, UsersIcon, CheckIcon, ErrorIcon } from "./Icons";
import {
  CardWrapper,
  CardBadge,
  CardTitle,
  CardSubtitle,
  CardSeats,
  FormRow,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FieldError,
  SubmitButton,
  FormNote,
  SuccessWrapper,
  SuccessIcon,
  SuccessTitle,
  SuccessText,
  SuccessBadge,
} from "../Styles";
import { StyledSelectFormControl } from "../../../common/CustomSelect/styles";
import AuthService from "../../../../services/AuthService";
import { useSnackbar } from "notistack";
import useLoader from "../../../../hooks/useLoader";
import useWebinar from "../../../../hooks/useWebinar";

const PROFESSION_OPTIONS = [
  "Advocate / Lawyer",
  "Arbitrator / Mediator",
  "Corporate Legal / In-House Counsel",
  "Judiciary",
  "Law Student",
  "Business Owner / Entrepreneur",
  "Academic / Researcher",
  "Government Official",
  "Other",
];

const INITIAL_FORM_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  profession: "",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[\d\s\-()]{7,16}$/;
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]{2,50}$/;

function validateFormFields(values, fieldConfig) {
  const errors = {};

  if (fieldConfig.firstName?.show && fieldConfig.firstName?.required) {
    if (!values.firstName.trim()) errors.firstName = "First name is required";
    else if (!NAME_REGEX.test(values.firstName.trim()))
      errors.firstName = "Enter a valid first name (2–50 letters)";
  }

  if (fieldConfig.lastName?.show && fieldConfig.lastName?.required) {
    if (!values.lastName.trim()) errors.lastName = "Last name is required";
    else if (!NAME_REGEX.test(values.lastName.trim()))
      errors.lastName = "Enter a valid last name (2–50 letters)";
  }

  if (fieldConfig.email?.show && fieldConfig.email?.required) {
    if (!values.email.trim()) errors.email = "Email address is required";
    else if (!EMAIL_REGEX.test(values.email.trim()))
      errors.email = "Enter a valid email address";
  }

  if (fieldConfig.phone?.show && fieldConfig.phone?.required) {
    if (!values.phone.trim()) errors.phone = "Phone number is required";
    else if (!PHONE_REGEX.test(values.phone.trim()))
      errors.phone = "Enter a valid phone number";
  }

  if (fieldConfig.profession?.show && fieldConfig.profession?.required) {
    if (!values.profession) errors.profession = "Please select your profession";
  }

  return errors;
}

function FormField({ label, required, error, children }) {
  return (
    <FormGroup>
      <FormLabel>
        {label}
        {required && <span style={{ color: "#ff3c3c", marginLeft: 2 }}>*</span>}
      </FormLabel>
      {children}
      {error && (
        <FieldError>
          <ErrorIcon size={11} />
          {error}
        </FieldError>
      )}
    </FormGroup>
  );
}

function RegistrationForm() {
  const { config } = useWebinar();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formConfig = config?.registrationForm;
  const fieldConfig = formConfig?.fields ?? {};

  const getFieldError = useCallback(
    (fieldName) =>
      touchedFields[fieldName]
        ? validateFormFields(formValues, fieldConfig)[fieldName]
        : undefined,
    [formValues, touchedFields, fieldConfig],
  );

  if (!formConfig?.show) return null;

  const handleFieldChange = (fieldName) => (e) => {
    const value = e?.target?.value ?? e; // fallback if not event
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleFieldBlur = (fieldName) => () =>
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const allFieldsTouched = Object.keys(INITIAL_FORM_VALUES).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {},
    );
    setTouchedFields(allFieldsTouched);

    const validationErrors = validateFormFields(formValues, fieldConfig);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      setLoader({ state: true, message: "Submitting your registration..." });
      const response = await AuthService.webinar(formValues);
      if (response?.message) {
        enqueueSnackbar(response.message, { variant: "success" });
      }
      setIsSubmitted(true);
      setFormValues(INITIAL_FORM_VALUES);
    } catch (error) {
      enqueueSnackbar(
        error?.message || "Something went wrong. Please try again.",
        { variant: "error" },
      );
    } finally {
      setIsSubmitting(false);
      setLoader({ state: false });
    }
  };

  if (isSubmitted) {
    return (
      <CardWrapper id="register">
        <SuccessWrapper>
          <SuccessIcon>
            <CheckIcon size={30} />
          </SuccessIcon>
          <SuccessTitle>You're Registered!</SuccessTitle>
          <SuccessText>
            Thank you for registering. We'll send the event details shortly.
            Joining link will be shared 48 hours before the event.
          </SuccessText>
          <SuccessBadge>{formConfig.successDate}</SuccessBadge>
        </SuccessWrapper>
      </CardWrapper>
    );
  }

  const showNameRow = fieldConfig.firstName?.show || fieldConfig.lastName?.show;

  return (
    <CardWrapper id="register">
      <CardBadge>{formConfig.badge}</CardBadge>
      <CardTitle>{formConfig.title}</CardTitle>
      <CardSubtitle>{formConfig.subtitle}</CardSubtitle>
      <CardSeats>
        <UsersIcon size={12} />
        {formConfig.seatsLabel}
      </CardSeats>

      <form onSubmit={handleFormSubmit} noValidate>
        {showNameRow && (
          <FormRow>
            {fieldConfig.firstName?.show && (
              <FormField
                label="First Name"
                required={fieldConfig.firstName.required}
                error={getFieldError("firstName")}
              >
                <FormInput
                  type="text"
                  placeholder={
                    fieldConfig.firstName.placeholder ?? "First name"
                  }
                  value={formValues.firstName}
                  onChange={handleFieldChange("firstName")}
                  onBlur={handleFieldBlur("firstName")}
                  $hasError={!!getFieldError("firstName")}
                  autoComplete="given-name"
                />
              </FormField>
            )}
            {fieldConfig.lastName?.show && (
              <FormField
                label="Last Name"
                required={fieldConfig.lastName.required}
                error={getFieldError("lastName")}
              >
                <FormInput
                  type="text"
                  placeholder={fieldConfig.lastName.placeholder ?? "Last name"}
                  value={formValues.lastName}
                  onChange={handleFieldChange("lastName")}
                  onBlur={handleFieldBlur("lastName")}
                  $hasError={!!getFieldError("lastName")}
                  autoComplete="family-name"
                />
              </FormField>
            )}
          </FormRow>
        )}

        {fieldConfig.email?.show && (
          <FormField
            label="Email Address"
            required={fieldConfig.email.required}
            error={getFieldError("email")}
          >
            <FormInput
              type="email"
              placeholder={fieldConfig.email.placeholder ?? "email@example.com"}
              value={formValues.email}
              onChange={handleFieldChange("email")}
              onBlur={handleFieldBlur("email")}
              $hasError={!!getFieldError("email")}
              autoComplete="email"
            />
          </FormField>
        )}

        {fieldConfig.phone?.show && (
          <FormField
            label="Phone Number"
            required={fieldConfig.phone.required}
            error={getFieldError("phone")}
          >
            <FormInput
              type="tel"
              placeholder={fieldConfig.phone.placeholder ?? "+91 00000 00000"}
              value={formValues.phone}
              onChange={handleFieldChange("phone")}
              onBlur={handleFieldBlur("phone")}
              $hasError={!!getFieldError("phone")}
              autoComplete="tel"
            />
          </FormField>
        )}

        {fieldConfig.profession?.show && (
          <FormField
            label="Profession / Role"
            required={fieldConfig.profession.required}
            error={getFieldError("profession")}
          >
            <StyledSelectFormControl className="input-white" variant="outlined">
              <FormSelect
                value={formValues.profession}
                onChange={handleFieldChange("profession")}
                onBlur={handleFieldBlur("profession")}
                $hasError={!!getFieldError("profession")}
                $hasValue={!!formValues.profession}
              >
                <option value="" disabled>
                  Select your profession
                </option>
                {PROFESSION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </FormSelect>
            </StyledSelectFormControl>
          </FormField>
        )}

        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Registering…" : "Register Now"}
        </SubmitButton>

        <FormNote>
          <LockIcon size={11} />
          Your information is secure and will not be shared.
        </FormNote>
      </form>
    </CardWrapper>
  );
}

export { RegistrationForm };
